import React, { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  User,
  Lock,
  GraduationCap,
  SignOut,
  Phone,
  Calendar,
} from "@phosphor-icons/react";
import Container from "Components/Container/Container";
import { LoginContext } from "context/Auth/LoginContext";
import { CartContext } from "context/Cart/CartContext";
import usePost from "Hooks/usePost";
import Spinner from "Components/RequestHandler/Spinner";
import EditProfile from "./EditProfile";
import ChangePassword from "./ChangePassword";
import OwnedClasses from "./OwnedClasses";
// import OwnedBooks from "./OwnedBooks"; // Hidden for now

const MyAccount = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, setIsLoggedIn, setUser, setIsVerified, setToken, clearStorage } =
    useContext(LoginContext);
  const { setCart } = useContext(CartContext);
  const { postData, isLoading } = usePost();
  const [activeTab, setActiveTab] = useState("profile");

  const handleLogout = async () => {
    const response = await postData("auth/logout");
    if (response?.status === 200) {
      setIsLoggedIn(false);
      setUser(null);
      setIsVerified(false);
      setToken(null);
      clearStorage();
      setCart([]);
      navigate("/");
    }
  };

  const tabs = [
    { id: "profile", label: t("account.profile"), icon: User },
    { id: "security", label: t("account.changePassword"), icon: Lock },
    { id: "courses", label: t("account.myCourses"), icon: GraduationCap },
    // { id: "books", label: t("account.myBooks"), icon: Book }, // Hidden for now
  ];

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <main className="py-8 md:py-12 bg-gray-50 min-h-screen">
      <Container>
        {/* Profile Header Card */}
        <div className="bg-gradient-to-br from-primary via-primary to-primaryDark rounded-2xl shadow-sm overflow-hidden mb-8">
          <div className="px-4 md:px-8 py-6 md:py-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              {/* Avatar & Name */}
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/10 p-0.5 flex-shrink-0">
                  <div className="w-full h-full rounded-full bg-white/15 backdrop-blur-sm flex items-center justify-center">
                    <span className="text-2xl md:text-3xl font-bold text-white/90">
                      {getInitials(user?.fullName)}
                    </span>
                  </div>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-white/95">
                    {user?.fullName}
                  </h1>
                  <p className="text-white/70 text-sm md:text-base mt-1">
                    {user?.email}
                  </p>
                  <div className="mt-2">
                    {user?.verified ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/10 text-white/90 text-xs font-medium rounded-full">
                        <span className="w-1.5 h-1.5 bg-green-400 rounded-full"></span>
                        {t("common.verified") || "Verified Account"}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/10 text-white/90 text-xs font-medium rounded-full">
                        <span className="w-1.5 h-1.5 bg-orange-400 rounded-full"></span>
                        {t("common.unverified") || "Unverified Account"}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Logout Button */}
              <div>
                <button
                  onClick={handleLogout}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/15 text-white/90 rounded-lg transition-colors duration-200 font-medium"
                >
                  {isLoading ? (
                    <Spinner isSmall />
                  ) : (
                    <>
                      <SignOut size={20} />
                      {t("common.logout")}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* User Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-white/10">
              <div className="flex items-center gap-3 text-white/70">
                <Phone size={20} className="text-white/50" />
                <span className="text-sm">{user?.phoneNumber || "—"}</span>
              </div>
              <div className="flex items-center gap-3 text-white/70">
                <Calendar size={20} className="text-white/50" />
                <span className="text-sm">
                  {t("account.memberSince") || "Member since"}: {formatDate(user?.createdAt)}
                </span>
              </div>
              <div className="flex items-center gap-3 text-white/70">
                <GraduationCap size={20} className="text-white/50" />
                <span className="text-sm">
                  {user?.purchasedItems?.courses?.length || 0} {t("account.myCourses")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-2xl shadow-sm mb-8">
          <div className="flex overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 font-medium text-sm md:text-base whitespace-nowrap transition-colors duration-200 border-b-2 ${
                    activeTab === tab.id
                      ? "text-primary border-primary bg-primary/5"
                      : "text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <Icon size={20} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="bg-white rounded-2xl shadow-sm p-4 md:p-8">
          {activeTab === "profile" && <EditProfile />}
          {activeTab === "security" && <ChangePassword />}
          {activeTab === "courses" && <OwnedClasses />}
          {/* {activeTab === "books" && <OwnedBooks />} */}
        </div>
      </Container>
    </main>
  );
};

export default MyAccount;
